import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { Capacitor, registerPlugin } from '@capacitor/core'

/**
 * Light or dark, or whatever the device is doing.
 *
 * The palette is semantic — `text-ink`, `bg-surface`, `border-stone` — so
 * the whole app turns over by adding one class to `<html>`; see the dark
 * block in main.css. The choice is this browser's, kept in localStorage,
 * because it is a preference about a screen rather than anything the
 * household shares.
 */
export type ThemeChoice = 'system' | 'light' | 'dark'

const KEY = 'theme'

/** Read before Vue exists too, by the inline script in index.html. */
export function storedChoice(): ThemeChoice {
  try {
    const saved = localStorage.getItem(KEY)
    return saved === 'light' || saved === 'dark' ? saved : 'system'
  } catch {
    return 'system'
  }
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

interface StatusBarPlugin {
  setStyle(options: { style: 'DARK' | 'LIGHT' }): Promise<void>
  setBackgroundColor(options: { color: string }): Promise<void>
}
const StatusBar = registerPlugin<StatusBarPlugin>('StatusBar')

/** Page background, as the dark block in main.css defines it. */
const BAR = { light: '#FAFAF7', dark: '#141715' }

export const useThemeStore = defineStore('theme', () => {
  const choice = ref<ThemeChoice>(storedChoice())
  const systemDark = ref(systemPrefersDark())

  const dark = computed(() => (choice.value === 'system' ? systemDark.value : choice.value === 'dark'))

  function paint() {
    document.documentElement.classList.toggle('dark', dark.value)
    if (!Capacitor.isNativePlatform()) return
    // The phone's own bar sits above our header, so it has to follow too, or
    // a dark app wears a white hat.
    void Promise.allSettled([
      // Style names the *content*: dark content on a light bar, and back.
      StatusBar.setStyle({ style: dark.value ? 'DARK' : 'LIGHT' }),
      StatusBar.setBackgroundColor({ color: dark.value ? BAR.dark : BAR.light }),
    ])
  }

  function set(next: ThemeChoice) {
    choice.value = next
    try {
      if (next === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, next)
    } catch {
      // A browser with storage refused still gets the theme for this session.
    }
    paint()
  }

  /** Called once by the shell; following the system means listening to it. */
  function start() {
    paint()
    if (typeof matchMedia !== 'function') return
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
      systemDark.value = event.matches
      if (choice.value === 'system') paint()
    })
  }

  return { choice, dark, set, start }
})
